import React from 'react';
import { Link } from 'react-router-dom';
import { Logo } from './Logo';
import { useBrandName } from './useBrandName';
import { useDiscreet } from '../../security/discreet';

export interface HeaderBrandProps {
  /** Makes the brand a link (the app header goes to /dashboard, public headers to /). */
  to?: string;
  /** Wordmark height from `sm` up; phones get the icon tile. */
  wordmarkHeight?: number;
  iconHeight?: number;
  className?: string;
}

/**
 * Brand area of a header: the stacked wordmark on `sm` and wider, the icon
 * tile on phones. The pair is one named image (or link): the artwork inside is
 * decorative so assistive technology reads the name once. In discreet mode it
 * is the plain text "Notes".
 */
export const HeaderBrand: React.FC<HeaderBrandProps> = ({
  to,
  wordmarkHeight = 40,
  iconHeight = 36,
  className = '',
}) => {
  const name = useBrandName();
  const discreet = useDiscreet();

  const content = discreet ? (
    <span className="truncate font-display text-lg font-bold text-white sm:text-2xl">
      {name}
    </span>
  ) : (
    <>
      <Logo
        variant="icon"
        height={iconHeight}
        decorative
        className="sm:hidden"
      />
      <Logo
        variant="wordmark"
        height={wordmarkHeight}
        decorative
        className="hidden sm:block"
      />
    </>
  );

  const base = `flex min-w-0 shrink-0 items-center ${className}`.trim();

  if (to) {
    return (
      <Link
        to={to}
        aria-label={name}
        data-testid="header-brand"
        className={`${base} min-h-[44px] rounded-tile focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hc-accent`}
      >
        {content}
      </Link>
    );
  }
  return (
    <div
      role="img"
      aria-label={name}
      data-testid="header-brand"
      className={base}
    >
      {content}
    </div>
  );
};

export default HeaderBrand;
